import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { TRUCK_TYPES } from "@/lib/constants";
import { PasteAutoFill } from "@/components/PasteAutoFill";

export default function PostTruckDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    current_location: "", destination: "", truck_type: "", capacity: "",
    temp_min: "-25", temp_max: "20", available_date: "", notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const applyFilled = (fields) => setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== "")) }));

  const postAll = async (items) => {
    let ok = 0, skip = 0;
    for (const it of items) {
      if (!it.current_location || !it.truck_type || !it.capacity || !it.available_date) { skip++; continue; }
      try {
        await api.post("/trucks", {
          current_location: it.current_location, destination: it.destination || "",
          truck_type: it.truck_type, capacity: parseFloat(it.capacity),
          temp_min: it.temp_min !== "" ? parseFloat(it.temp_min) : -25,
          temp_max: it.temp_max !== "" ? parseFloat(it.temp_max) : 20,
          available_date: it.available_date, notes: it.notes || "",
        });
        ok++;
      } catch (e) { skip++; }
    }
    if (ok) toast.success(`Posted ${ok} truck${ok > 1 ? "s" : ""}${skip ? ` · ${skip} skipped (missing details)` : ""}`);
    else { toast.error("Couldn't post — entries were missing required details."); throw new Error("none posted"); }
    setOpen(false);
    onCreated?.();
  };

  const submit = async () => {
    if (!form.current_location || !form.truck_type || !form.capacity || !form.available_date) {
      toast.error("Please fill location, truck type, capacity and available date.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/trucks", {
        ...form,
        capacity: parseFloat(form.capacity),
        temp_min: parseFloat(form.temp_min),
        temp_max: parseFloat(form.temp_max),
      });
      toast.success("Truck availability posted");
      setOpen(false);
      setForm({ current_location: "", destination: "", truck_type: "", capacity: "", temp_min: "-25", temp_max: "20", available_date: "", notes: "" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post truck");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-post-truck-btn" className="rounded-full cta-accent font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Post a Truck
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Post Truck Availability</DialogTitle>
          <DialogDescription>Broadcast your reefer truck so shippers can book it fast.</DialogDescription>
        </DialogHeader>
        <div className="pt-1"><PasteAutoFill kind="truck" onFilled={applyFilled} onPostAll={postAll} /></div>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5 col-span-2">
            <Label>Current location</Label>
            <Input data-testid="truck-location-input" value={form.current_location} onChange={(e) => set("current_location", e.target.value)} placeholder="e.g. Nashik" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Heading to (optional)</Label>
            <Input data-testid="truck-destination-input" value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="e.g. Delhi — surfaces you for loads on this route" />
          </div>
          <div className="space-y-1.5">
            <Label>Truck type</Label>
            <Select value={form.truck_type} onValueChange={(v) => set("truck_type", v)}>
              <SelectTrigger data-testid="truck-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{TRUCK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Capacity (tonnes)</Label>
            <Input data-testid="truck-capacity-input" type="number" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="e.g. 15" />
          </div>
          <div className="space-y-1.5">
            <Label>Temp min (°C)</Label>
            <Input data-testid="truck-tempmin-input" type="number" value={form.temp_min} onChange={(e) => set("temp_min", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Temp max (°C)</Label>
            <Input data-testid="truck-tempmax-input" type="number" value={form.temp_max} onChange={(e) => set("temp_max", e.target.value)} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Available date</Label>
            <Input data-testid="truck-date-input" type="date" value={form.available_date} onChange={(e) => set("available_date", e.target.value)} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Notes (optional)</Label>
            <Textarea data-testid="truck-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Preferred routes, rate…" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground px-1">Your truck stays live for 2 days, or until you mark it as loaded.</p>
        <DialogFooter>
          <Button data-testid="submit-truck-btn" onClick={submit} disabled={saving} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 w-full">
            {saving ? "Posting…" : "Post Truck"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
